import { useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { logActivity } from './useActivityLog';
import { getStripeEnvironment } from '@/lib/stripe';

/**
 * Resync Stripe subscription quantities to current active facility count.
 * Awaited and throws on failure so the caller can compensate (e.g. roll back
 * the just-inserted site row).
 */
async function syncStripeQuantities(): Promise<{ ok: boolean; errors?: any[] }> {
  const environment = (() => {
    try { return getStripeEnvironment(); } catch { return 'sandbox' as const; }
  })();
  const { data, error } = await supabase.functions.invoke('update-module-quantity', {
    body: { environment },
  });
  if (error) throw new Error(error.message || 'Stripe sync failed');
  if (data && data.ok === false) {
    const msg = (data.errors || []).map((e: any) => e?.error).filter(Boolean).join('; ') || 'Stripe sync failed';
    throw new Error(msg);
  }
  return { ok: true };
}


interface RoomTypeConfig {
  singleRooms?: number;
  sharedRooms?: number;
  communalRooms?: number;
  communalBeds?: number;
  startingNumber?: number;
}

interface FacilityUpdates {
  name?: string;
  address?: string;
  phone?: string;
  capacity?: number;
  is_active?: boolean;
  is_family_shelter?: boolean;
  room_total?: number;
  room_prefixes?: string;
  rooms_per_floor?: number;
  allow_sub_rooms?: boolean;
}

interface FacilityCreateData {
  name: string;
  address?: string;
  phone?: string;
  capacity?: number;
  is_family_shelter?: boolean;
  room_total?: number;
  room_prefixes?: string;
  rooms_per_floor?: number;
  allow_sub_rooms?: boolean;
  organization_id?: string;
}

async function generateRoomsFromConfig(siteId: string, config: RoomTypeConfig) {
  // Check existing rooms first
  const { data: existingRooms } = await supabase
    .from('rooms')
    .select('id')
    .eq('site_id', siteId);
  
  if (existingRooms && existingRooms.length > 0) {
    return; // Rooms already exist, skip generation
  }

  const rooms: any[] = [];
  let roomNum = config.startingNumber || 101;

  // Generate single rooms
  const singleCount = config.singleRooms || 0;
  for (let i = 0; i < singleCount; i++) {
    rooms.push({
      site_id: siteId,
      room_number: String(roomNum),
      capacity: 1,
      current_occupancy: 0,
      is_occupied: false,
      room_type: 'single',
    });
    roomNum++;
  }

  // Generate shared rooms (a/b pairs)
  const sharedCount = config.sharedRooms || 0;
  for (let i = 0; i < sharedCount; i++) {
    rooms.push({
      site_id: siteId,
      room_number: `${roomNum}a`,
      capacity: 1,
      current_occupancy: 0,
      is_occupied: false,
      room_type: 'shared',
    });
    rooms.push({
      site_id: siteId,
      room_number: `${roomNum}b`,
      capacity: 1,
      current_occupancy: 0,
      is_occupied: false,
      room_type: 'shared',
    });
    roomNum++;
  }

  // Generate communal rooms
  const communalCount = config.communalRooms || 0;
  const communalBeds = config.communalBeds || 4;
  for (let i = 0; i < communalCount; i++) {
    rooms.push({
      site_id: siteId,
      room_number: String(roomNum),
      capacity: communalBeds,
      current_occupancy: 0,
      is_occupied: false,
      room_type: 'communal',
    });
    roomNum++;
  }

  if (rooms.length > 0) {
    const { error } = await supabase.from('rooms').insert(rooms);
    if (error) {
      console.error('Failed to generate rooms:', error);
      throw error;
    }
  }
}

// Legacy function for backward compatibility
async function generateRoomsForFacility(siteId: string, roomTotal: number, allowSubRooms: boolean = false) {
  if (allowSubRooms) {
    await generateRoomsFromConfig(siteId, { sharedRooms: roomTotal, startingNumber: 101 });
  } else {
    await generateRoomsFromConfig(siteId, { singleRooms: roomTotal, startingNumber: 101 });
  }
}

export const useFacilityManagement = () => {
  const queryClient = useQueryClient();

  const updateFacility = useMutation({
    mutationFn: async ({ 
      id, 
      updates,
      roomConfig
    }: { 
      id: string; 
      updates: FacilityUpdates;
      roomConfig?: RoomTypeConfig;
    }) => {
      const { data, error } = await supabase
        .from('sites')
        .update(updates)
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;

      if (roomConfig) {
        await generateRoomsFromConfig(id, roomConfig);
      } else if (updates.room_total && updates.room_total > 0) {
        await generateRoomsForFacility(id, updates.room_total, updates.allow_sub_rooms ?? false);
      }

      // If is_active was toggled (archive/restore), atomically resync Stripe.
      // Roll back the toggle if Stripe rejects the new quantity.
      if (typeof updates.is_active === 'boolean') {
        try {
          await syncStripeQuantities();
        } catch (syncErr: any) {
          await supabase
            .from('sites')
            .update({ is_active: !updates.is_active })
            .eq('id', id);
          throw new Error(
            `Stripe billing sync failed: ${syncErr?.message || 'unknown error'}. Change reverted.`,
          );
        }
      }

      return data;
    },
    onSuccess: async (data) => {
      queryClient.invalidateQueries({ queryKey: ['sites'] });
      queryClient.invalidateQueries({ queryKey: ['rooms'] });



      await logActivity({
        actionType: 'manage_access',
        actionDetails: {
          type: 'facility_update',
          site_id: data.id
        },
        siteId: data.id
      });

      toast.success('Facility updated successfully');
    },
    onError: (error: any) => {
      toast.error(error.message || 'Failed to update facility');
    }
  });

  const createFacility = useMutation({
    mutationFn: async ({ roomConfig, ...facilityData }: FacilityCreateData & { roomConfig?: RoomTypeConfig }) => {
      let organizationId = facilityData.organization_id;

      if (!organizationId) {
        const { data: userData, error: userErr } = await supabase.auth.getUser();
        if (userErr || !userData?.user) {
          throw new Error('You must be signed in to create a facility.');
        }
        const { data: orgMember, error: orgErr } = await supabase
          .from('organization_members')
          .select('organization_id')
          .eq('user_id', userData.user.id)
          .limit(1)
          .maybeSingle();
        if (orgErr) throw orgErr;
        if (!orgMember?.organization_id) {
          throw new Error('No organization is linked to your account. Ask an owner to add you to an organization before creating a facility.');
        }
        organizationId = orgMember.organization_id;
      }

      const { data, error } = await supabase
        .from('sites')
        .insert({ ...facilityData, organization_id: organizationId })
        .select()
        .single();

      if (error) throw error;

      if (roomConfig) {
        await generateRoomsFromConfig(data.id, roomConfig);
      } else if (facilityData.room_total && facilityData.room_total > 0) {
        await generateRoomsForFacility(data.id, facilityData.room_total, facilityData.allow_sub_rooms ?? false);
      }

      // Atomic Stripe sync: if it fails, roll back the site insert so we
      // don't leave a facility on the books that isn't on the bill.
      try {
        await syncStripeQuantities();
      } catch (syncErr: any) {
        await supabase.from('sites').delete().eq('id', data.id);
        throw new Error(
          `Stripe billing sync failed: ${syncErr?.message || 'unknown error'}. The facility was not created.`,
        );
      }

      return data;
    },
    onSuccess: async (data) => {
      queryClient.invalidateQueries({ queryKey: ['sites'] });
      queryClient.invalidateQueries({ queryKey: ['rooms'] });

      // Stripe quantity already synced in mutationFn (atomic).


      await logActivity({
        actionType: 'manage_access',
        actionDetails: {
          type: 'facility_creation',
          site_id: data.id
        },
        siteId: data.id
      });

      toast.success('Facility created successfully');
    },
    onError: (error: any) => {
      toast.error(error.message || 'Failed to create facility');
    }
  });

  return {
    updateFacility: updateFacility.mutate,
    createFacility: createFacility.mutate,
    isUpdating: updateFacility.isPending,
    isCreating: createFacility.isPending
  };
};

export type { RoomTypeConfig };
